/**
 * Pure Muushig (Муушиг) state helpers.
 *
 * Rules:
 * 1. Setup & Deck: 2–5 players, 32-card deck (7, 8, 9, 10, J, Q, K, A across all 4 suits).
 *    Each player is dealt 5 cards. One card is turned face-up in the center to determine the
 *    trump suit (`trumpCard`), and the remaining cards form the stock pile (`stock`).
 * 2. Decisions precede exchanges; at least two players stay. Active players exchange
 *    0–5 cards in order after the dealer. The dealer may then take the face-up trump.
 * 3. Trick-Taking (`stage === 'playing'`):
 *    - Leader plays 1 card.
 *    - Following players must follow the led suit if they hold a card of that suit.
 *      If void, they must trump if possible, and must beat the winner when possible.
 *    - Lead the trump ace when held; middle hands must use a winning led-suit ace.
 *    - Highest card of the led suit wins the trick, unless a trump card is played (highest trump wins).
 * 4. Scoring & Penalties:
 *    - Players start at 15 points and count down toward 0.
 *    - Winning `t >= 1` tricks reduces a player's score by `t` (`-t`).
 *    - Failing to win any tricks (`0` tricks while active) incurs the "Muushig" penalty (`+5` points).
 *    - Resigning before the round (`folded`) incurs `0` change (avoids the +5 penalty).
 */
import {
  type Card,
  type Rank,
  type Rng,
  type Suit,
  SUITS,
  normalizeCard,
  rankIndex,
  shuffle,
  sortByRank,
  suitIndex,
  suitOf,
  cryptoRng,
} from './cards.js';
import type { GameFormat, GameView, MuushigTrickCard, MuushigView, PauseView, PublicPlayer, SeatAction, SeatCount } from './protocol.js';
import { MUUSHIG_PENALTY } from './protocol.js';
import type { GameState, Result } from './game.js';

export const MUUSHIG_RANKS: readonly Rank[] = ['7', '8', '9', 'T', 'J', 'Q', 'K', 'A'] as const;

export const MUUSHIG_REMOVED: Card[] = (() => {
  const removed: Card[] = [];
  for (const r of ['3', '4', '5', '6', '2'] as const) {
    for (const s of SUITS) removed.push(r + s);
  }
  return removed;
})();

export function muushigDeck(): Card[] {
  const deck: Card[] = [];
  for (const r of MUUSHIG_RANKS) {
    for (const s of SUITS) {
      deck.push(r + s);
    }
  }
  return deck;
}

export interface MuushigState {
  stage: 'decision' | 'exchange' | 'trump-exchange' | 'playing';
  trumpCard: Card;
  trumpSuit: Suit;
  stock: Card[];
  trickCards: MuushigTrickCard[];
  lastTrick: {
    winnerPlayerId: string;
    cards: MuushigTrickCard[];
  } | null;
  tricksWon: number[];
  folded: boolean[];
  exchanged: boolean[];
  decided: boolean[];
  dealerSeat: number;
  scores: number[];
  fourAcesWinner: number | null;
}

export function muushigFormat(seats: SeatCount): GameFormat {
  return {
    seats,
    cardsEach: 5,
    passesToClear: Math.max(1, seats - 1),
    removed: [...MUUSHIG_REMOVED],
  };
}

export function createMuushigGame(
  playerIds: string[],
  rng: Rng = cryptoRng,
  options?: { dealerSeat?: number; muushigScores?: number[] }
): GameState {
  const seats = playerIds.length as SeatCount;
  if (seats < 2 || seats > 5) {
    throw new Error(`Unsupported Muushig seat count: ${playerIds.length}`);
  }
  const format = muushigFormat(seats);
  const deck = shuffle(muushigDeck(), rng);

  const hands: Card[][] = Array.from({ length: seats }, () => []);
  for (let c = 0; c < 5; c++) {
    for (let s = 0; s < seats; s++) {
      hands[s].push(deck[c * seats + s]);
    }
  }
  for (let s = 0; s < seats; s++) {
    hands[s] = sortByRank(hands[s]);
  }
  const trumpCard = deck[seats * 5];
  const stock = deck.slice(seats * 5 + 1);

  const dealerSeat = options?.dealerSeat ?? seats - 1;
  const scores = options?.muushigScores ?? playerIds.map(() => 15);

  return {
    gameType: 'muushig',
    format,
    playerIds: [...playerIds],
    hands,
    dead: [...stock],
    phase: 'playing',
    turn: (dealerSeat + 1) % seats,
    trick: null,
    passes: 0,
    lastActions: playerIds.map(() => null),
    mustInclude: null,
    winner: null,
    muushig: {
      stage: 'decision',
      trumpCard,
      trumpSuit: suitOf(trumpCard),
      stock,
      trickCards: [],
      lastTrick: null,
      tricksWon: playerIds.map(() => 0),
      folded: playerIds.map(() => false),
      exchanged: playerIds.map(() => false),
      decided: playerIds.map(() => false),
      dealerSeat,
      scores,
      fourAcesWinner: null,
    },
  };
}

export function muushigCardBeats(challenger: Card, currentBest: Card, trumpSuit: Suit): boolean {
  const cSuit = suitOf(challenger);
  const bSuit = suitOf(currentBest);
  if (cSuit === bSuit) {
    return rankIndex(challenger) > rankIndex(currentBest);
  }
  if (cSuit === trumpSuit && bSuit !== trumpSuit) {
    return true;
  }
  return false;
}

export function winningMuushigPlay(
  trickCards: readonly MuushigTrickCard[],
  trumpSuit: Suit,
): MuushigTrickCard {
  let best = trickCards[0];
  for (let i = 1; i < trickCards.length; i++) {
    if (muushigCardBeats(trickCards[i].card, best.card, trumpSuit)) {
      best = trickCards[i];
    }
  }
  return best;
}

export function legalMuushigCards(
  hand: readonly Card[],
  trumpSuit: Suit,
  trickCards: readonly MuushigTrickCard[],
  activePlayers: number,
): Card[] {
  const trumpAce = 'A' + trumpSuit;
  if (trickCards.length === 0) {
    return hand.includes(trumpAce) ? [trumpAce] : [...hand];
  }
  const leadSuit = suitOf(trickCards[0].card);
  const following = hand.filter((card) => suitOf(card) === leadSuit);
  const trumps = hand.filter((card) => suitOf(card) === trumpSuit);
  const candidates = following.length > 0 ? following : trumps.length > 0 ? trumps : [...hand];
  const best = winningMuushigPlay(trickCards, trumpSuit).card;
  const ledAce = 'A' + leadSuit;
  if (trickCards.length < activePlayers - 1 && candidates.includes(ledAce)
    && muushigCardBeats(ledAce, best, trumpSuit)) {
    return [ledAce];
  }
  const beating = candidates.filter((card) => muushigCardBeats(card, best, trumpSuit));
  return beating.length > 0 ? beating : candidates;
}

function nextActiveSeat(folded: readonly boolean[], fromSeat: number): number {
  const n = folded.length;
  for (let step = 1; step <= n; step++) {
    const candidate = (fromSeat + step) % n;
    if (!folded[candidate]) return candidate;
  }
  return fromSeat;
}

function pickRoundWinner(tricksWon: readonly number[], folded: readonly boolean[], lastTrickWinner: number): number {
  let bestSeat = lastTrickWinner;
  let bestCount = -1;
  for (let s = 0; s < tricksWon.length; s++) {
    if (folded[s]) continue;
    if (tricksWon[s] > bestCount) {
      bestCount = tricksWon[s];
      bestSeat = s;
    }
  }
  return bestSeat;
}

export function canFoldMuushig(folded: readonly boolean[]): boolean {
  return folded.filter((isFolded) => !isFolded).length > 2;
}

function decideMuushig(state: GameState, seat: number, fold: boolean): Result {
  const current = state.muushig!;
  const folded = current.folded.map((value, index) => index === seat ? fold : value);
  const decided = current.decided.map((value, index) => index === seat ? true : value);
  const complete = decided.every(Boolean);
  const turn = complete ? nextActiveSeat(folded, current.dealerSeat) : (seat + 1) % state.format.seats;
  const nextState: GameState = {
    ...state,
    turn,
    hands: state.hands.map((hand, index) => fold && index === seat ? [] : hand),
    muushig: {
      ...current,
      folded,
      decided,
      exchanged: [...folded],
      stage: complete ? 'exchange' : 'decision',
    },
  };
  return fold
    ? { ok: true, state: nextState, event: { type: 'pass', seat, trickCleared: false, nextLead: null } }
    : { ok: true, state: nextState, event: { type: 'play', seat, combo: { type: 'single', cards: [], key: [0, 0] }, remaining: state.hands[seat].length, won: false } };
}

function applyMuushigPlayStep(state: GameState, seat: number, rawCards: unknown): Result {
  if (state.phase !== 'playing' || !state.muushig) {
    return { ok: false, error: 'The round is over.' };
  }
  if (state.turn !== seat) {
    return { ok: false, error: 'It is not your turn.' };
  }
  if (!Array.isArray(rawCards) || rawCards.length > 5) {
    return { ok: false, error: 'Select between 0 and 5 cards.' };
  }

  const cards: Card[] = [];
  for (const raw of rawCards) {
    const c = normalizeCard(raw);
    if (!c) return { ok: false, error: 'Invalid card code.' };
    cards.push(c);
  }
  if (new Set(cards).size !== cards.length) {
    return { ok: false, error: 'Duplicate cards submitted.' };
  }
  const hand = state.hands[seat];
  if (!cards.every((c) => hand.includes(c))) {
    return { ok: false, error: 'You do not hold those cards.' };
  }

  const m = state.muushig;

  if (m.stage === 'decision') {
    if (cards.length !== 0) return { ok: false, error: 'Decide whether to play before exchanging cards.' };
    return decideMuushig(state, seat, false);
  }

  if (m.stage === 'trump-exchange') {
    if (seat !== m.dealerSeat || cards.length > 1) {
      return { ok: false, error: 'The dealer may take the face-up trump for one card, or keep their hand.' };
    }
    const nextHands = state.hands.map((held, index) => index === seat && cards.length === 1
      ? sortByRank([...held.filter((card) => card !== cards[0]), m.trumpCard]) : held);
    return {
      ok: true,
      state: { ...state, hands: nextHands, turn: nextActiveSeat(m.folded, m.dealerSeat), muushig: { ...m, stage: 'playing' } },
      event: { type: 'play', seat, combo: { type: 'single', cards: cards.length === 1 ? [cards[0]] : [], key: [0, 0] }, remaining: 5, won: false },
    };
  }

  // ---- Stage 1: Card Replacement ('exchange') -----------------------------
  if (m.stage === 'exchange') {
    if (cards.length > m.stock.length) {
      return { ok: false, error: `Only ${m.stock.length} card(s) left in the stock pile.` };
    }
    const drawn = m.stock.slice(0, cards.length);
    const nextStock = m.stock.slice(cards.length);

    const kept = hand.filter((c) => !cards.includes(c));
    const nextHand = sortByRank([...kept, ...drawn]);
    const nextHands = state.hands.map((h, idx) => (idx === seat ? nextHand : h));
    const nextExchanged = m.exchanged.map((ex, idx) => (idx === seat ? true : ex));
    const lastActions = [...state.lastActions];
    lastActions[seat] = { type: 'play', cards: [] };

    const allExchanged = nextExchanged.every(Boolean);
    if (allExchanged) {
      const nextState: GameState = {
        ...state,
        hands: nextHands,
        dead: nextStock,
        turn: m.folded[m.dealerSeat] ? nextActiveSeat(m.folded, m.dealerSeat) : m.dealerSeat,
        lastActions: state.playerIds.map(() => null),
        muushig: {
          ...m,
          stage: m.folded[m.dealerSeat] ? 'playing' : 'trump-exchange',
          stock: nextStock,
          exchanged: nextExchanged,
        },
      };
      return {
        ok: true,
        state: nextState,
        event: {
          type: 'play',
          seat,
          combo: { type: 'single', cards: [], key: [0, 0] },
          remaining: nextHand.length,
          won: false,
        },
      };
    }

    const nextState: GameState = {
      ...state,
      hands: nextHands,
      dead: nextStock,
      turn: nextActiveSeat(m.folded, seat),
      lastActions,
      muushig: {
        ...m,
        stock: nextStock,
        exchanged: nextExchanged,
      },
    };
    return {
      ok: true,
      state: nextState,
      event: {
        type: 'play',
        seat,
        combo: { type: 'single', cards: [], key: [0, 0] },
        remaining: nextHand.length,
        won: false,
      },
    };
  }

  // ---- Stage 2: Trick-Taking ('playing') ----------------------------------
  if (cards.length !== 1) {
    return { ok: false, error: 'Select 1 card to play.' };
  }
  const card = cards[0];

  if (!legalMuushigCards(hand, m.trumpSuit, m.trickCards, m.folded.filter((folded) => !folded).length).includes(card)) {
    return { ok: false, error: 'You must follow the led suit, trump when void, and beat the winning card when possible; play a compulsory ace.' };
  }

  const nextHands = state.hands.map((h, idx) => (idx === seat ? h.filter((c) => c !== card) : h));
  const nextTrickCards: MuushigTrickCard[] = [
    ...m.trickCards,
    { playerId: state.playerIds[seat], seat, card },
  ];
  const activeSeatCount = m.folded.filter((f) => !f).length;
  const combo = { type: 'single' as const, cards: [card], key: [rankIndex(card), suitIndex(card)] };

  // Check if all active seats have played into this trick
  if (nextTrickCards.length >= activeSeatCount) {
    const bestPlay = winningMuushigPlay(nextTrickCards, m.trumpSuit);
    const trickWinner = bestPlay.seat;
    const nextTricksWon = m.tricksWon.map((t, idx) => (idx === trickWinner ? t + 1 : t));
    const lastTrick = {
      winnerPlayerId: state.playerIds[trickWinner],
      cards: nextTrickCards,
    };

    const roundOver = nextHands[trickWinner].length === 0;

    if (roundOver) {
      const matchWinner = m.scores.findIndex((score, index) => !m.folded[index] && score - nextTricksWon[index] <= 0);
      const roundWinner = matchWinner >= 0 ? matchWinner : pickRoundWinner(nextTricksWon, m.folded, trickWinner);
      const nextState: GameState = {
        ...state,
        hands: nextHands,
        phase: 'finished',
        turn: null,
        trick: null,
        winner: roundWinner,
        lastActions: state.playerIds.map(() => null),
        muushig: {
          ...m,
          trickCards: [],
          lastTrick,
          tricksWon: nextTricksWon,
        },
      };
      return {
        ok: true,
        state: nextState,
        event: {
          type: 'play',
          seat,
          combo,
          remaining: nextHands[seat].length,
          won: true,
        },
      };
    }

    const nextState: GameState = {
      ...state,
      hands: nextHands,
      turn: trickWinner,
      trick: null,
      lastActions: state.playerIds.map(() => null),
      muushig: {
        ...m,
        trickCards: [],
        lastTrick,
        tricksWon: nextTricksWon,
      },
    };
    return {
      ok: true,
      state: nextState,
      event: {
        type: 'play',
        seat,
        combo,
        remaining: nextHands[seat].length,
        won: false,
      },
    };
  }

  // Trick still in progress
  const bestSoFar = winningMuushigPlay(nextTrickCards, m.trumpSuit);
  const nextTurn = nextActiveSeat(m.folded, seat);
  const lastActions = [...state.lastActions];
  lastActions[seat] = { type: 'play', cards: [card] };

  const nextState: GameState = {
    ...state,
    hands: nextHands,
    turn: nextTurn,
    trick: {
      seat: bestSoFar.seat,
      combo: {
        type: 'single',
        cards: nextTrickCards.map((tc) => tc.card),
        key: [rankIndex(bestSoFar.card), suitIndex(bestSoFar.card)],
      },
    },
    lastActions,
    muushig: {
      ...m,
      trickCards: nextTrickCards,
    },
  };
  return {
    ok: true,
    state: nextState,
    event: {
      type: 'play',
      seat,
      combo,
      remaining: nextHands[seat].length,
      won: false,
    },
  };
}

export function applyMuushigPlay(state: GameState, seat: number, rawCards: unknown): Result {
  const result = applyMuushigPlayStep(state, seat, rawCards);
  if (!result.ok || state.muushig?.stage === 'playing') return result;
  const next = result.state;
  const winner = next.hands.findIndex((hand, index) => !next.muushig!.folded[index]
    && SUITS.every((suit) => hand.includes('A' + suit)));
  if (winner < 0) return result;
  return {
    ok: true,
    state: { ...next, phase: 'finished', turn: null, winner, muushig: { ...next.muushig!, stage: 'playing', fourAcesWinner: winner } },
    event: { type: 'play', seat, combo: { type: 'single', cards: [], key: [0, 0] }, remaining: next.hands[seat].length, won: true },
  };
}

export function applyMuushigPass(state: GameState, seat: number): Result {
  if (state.phase !== 'playing' || !state.muushig) {
    return { ok: false, error: 'The round is over.' };
  }
  if (state.turn !== seat) {
    return { ok: false, error: 'It is not your turn.' };
  }
  const m = state.muushig;
  if (m.stage !== 'decision') {
    return { ok: false, error: 'You can only sit out before card exchange begins.' };
  }
  if (SUITS.every((suit) => state.hands[seat].includes('A' + suit))) return applyMuushigPlay(state, seat, []);
  if (!canFoldMuushig(m.folded)) return { ok: false, error: 'At least two players must stay in the round.' };
  return decideMuushig(state, seat, true);
}

export function applyMuushigTimeout(state: GameState): Result {
  if (state.turn === null || !state.muushig) {
    return { ok: false, error: 'No active turn.' };
  }
  const seat = state.turn;
  const m = state.muushig;
  if (m.stage === 'decision' && canFoldMuushig(m.folded)) {
    return applyMuushigPass(state, seat);
  }
  if (m.stage === 'trump-exchange') {
    const hand = state.hands[seat];
    const sorted = [...hand].sort((first, second) =>
      Number(suitOf(first) === m.trumpSuit) - Number(suitOf(second) === m.trumpSuit) || rankIndex(first) - rankIndex(second));
    return applyMuushigPlay(state, seat, [sorted[0]]);
  }
  if (m.stage !== 'playing') return applyMuushigPlay(state, seat, []);
  const legal = legalMuushigCards(state.hands[seat], m.trumpSuit, m.trickCards, m.folded.filter((folded) => !folded).length);
  return applyMuushigPlay(state, seat, [legal[0]]);
}

/**
 * Official Muushig hand ordering:
 * 1. Cards matching the pot card's suit (trump suit) come FIRST, ordered descending (top to bottom: A -> K -> Q -> J -> T -> 9 -> 8 -> 7).
 * 2. The rest (non-trump cards) are ordered descending left to right (highest rank to lowest rank).
 */
export function sortMuushigHand(hand: readonly string[], trumpSuit: Suit): string[] {
  return [...hand].sort((a, b) => {
    const aIsTrump = suitOf(a) === trumpSuit;
    const bIsTrump = suitOf(b) === trumpSuit;
    if (aIsTrump && !bIsTrump) return -1;
    if (!aIsTrump && bIsTrump) return 1;
    const rankDiff = rankIndex(b) - rankIndex(a);
    if (rankDiff !== 0) return rankDiff;
    return suitIndex(b) - suitIndex(a);
  });
}

/**
 * Score delta per seat in Muushig (starting from 15 points and counting down to 0):
 * - Folded/resigned before the round: 0
 * - Active & 0 tricks won (Muushig!): +5 points
 * - Active & `t >= 1` tricks won: `-t` points
 */
export function computeMuushigScoreDeltas(state: GameState): number[] {
  const m = state.muushig;
  if (!m) return state.hands.map(() => 0);
  if (m.fourAcesWinner !== null) return m.scores.map((score, seat) => seat === m.fourAcesWinner ? -score : 0);
  return state.playerIds.map((_, seat) => {
    if (m.folded[seat]) return 0;
    const t = m.tricksWon[seat] ?? 0;
    if (t === 0) return MUUSHIG_PENALTY; // +5 penalty
    return -t;
  });
}

/**
 * Penalty per seat for round settlement & Token Room (Цай) payouts in Muushig:
 * - Winner seat: 0
 * - Folded seat: 0 (resigned before round to avoid penalty)
 * - Active seat with 0 tricks (Muushig!): 5
 * - Active non-winner with `t > 0` tricks: `Math.max(0, winnerTricks - t)`
 */
export function computeMuushigPenalties(state: GameState): number[] {
  const m = state.muushig;
  if (!m) return state.hands.map(() => 0);
  if (m.fourAcesWinner !== null) return state.hands.map(() => 0);
  const winnerSeat = state.winner ?? 0;
  const winnerTricks = m.tricksWon[winnerSeat] ?? 0;
  return state.playerIds.map((_, seat) => {
    if (seat === winnerSeat) return 0;
    if (m.folded[seat]) return 0;
    const t = m.tricksWon[seat] ?? 0;
    if (t === 0) return MUUSHIG_PENALTY;
    return Math.max(0, winnerTricks - t);
  });
}

export interface MuushigViewExtras {
  roomId: string;
  players: Omit<PublicPlayer, 'cardCount'>[];
  timeoutEndsAt: number | null;
  turnDurationMs?: number;
  pause?: PauseView | null;
  totals: Record<string, number>;
  serverTime?: number;
}

export function muushigViewFor(state: GameState, seat: number, extras: MuushigViewExtras): GameView {
  const id = (s: number) => state.playerIds[s];
  const lastActions: Record<string, SeatAction> = {};
  state.lastActions.forEach((a, s) => {
    if (a) lastActions[id(s)] = a.type === 'play' ? { type: 'play', cards: [...a.cards] } : { type: 'pass' };
  });

  let penalties: Record<string, number> | null = null;
  let scoreDeltas: Record<string, number> | null = null;
  if (state.phase === 'finished') {
    penalties = {};
    computeMuushigPenalties(state).forEach((p, s) => (penalties![id(s)] = p));
    scoreDeltas = {};
    computeMuushigScoreDeltas(state).forEach((d, s) => (scoreDeltas![id(s)] = d));
  }

  let muushigView: MuushigView | undefined;
  if (state.muushig) {
    const m = state.muushig;
    const tricksWon: Record<string, number> = {};
    m.tricksWon.forEach((count, s) => {
      tricksWon[id(s)] = count;
    });
    const foldedPlayerIds = m.folded.map((f, s) => (f ? id(s) : null)).filter(Boolean) as string[];
    const exchangedPlayerIds = m.exchanged.map((ex, s) => (ex ? id(s) : null)).filter(Boolean) as string[];
    const decidedPlayerIds = m.decided.map((d, s) => (d ? id(s) : null)).filter(Boolean) as string[];
    const scores: Record<string, number> = {};
    m.scores.forEach((sc, s) => {
      scores[id(s)] = sc;
    });
    muushigView = {
      stage: m.stage,
      trumpCard: m.trumpCard,
      trumpSuit: m.trumpSuit,
      stockCount: m.stock.length,
      trickCards: m.trickCards.map((tc) => ({ ...tc })),
      leadSuit: m.trickCards.length > 0 ? suitOf(m.trickCards[0].card) : null,
      lastTrick: m.lastTrick
        ? {
            winnerPlayerId: m.lastTrick.winnerPlayerId,
            cards: m.lastTrick.cards.map((tc) => ({ ...tc })),
          }
        : null,
      tricksWon,
      foldedPlayerIds,
      exchangedPlayerIds,
      decidedPlayerIds,
      scoreDeltas,
      dealerSeat: m.dealerSeat,
      scores,
    };
  }

  return {
    roomId: extras.roomId,
    gameType: 'muushig',
    phase: state.phase,
    format: { ...state.format, removed: [...state.format.removed] },
    myPlayerId: id(seat),
    mySeat: seat,
    myHand: extras.pause?.status === 'paused' ? [] : [...state.hands[seat]],
    players: extras.players.map((p) => ({
      ...p,
      cardCount: state.hands[p.seat].length,
      ...(state.muushig
        ? {
            tricksWon: state.muushig.tricksWon[p.seat] ?? 0,
            folded: state.muushig.folded[p.seat] ?? false,
            exchanged: state.muushig.exchanged[p.seat] ?? false,
            decided: state.muushig.decided[p.seat] ?? false,
          }
        : {}),
    })),
    activePlayerId: state.turn === null ? null : id(state.turn),
    timeoutEndsAt: extras.timeoutEndsAt,
    ...(extras.turnDurationMs !== undefined ? { turnDurationMs: extras.turnDurationMs } : {}),
    pause: extras.pause ?? null,
    trick: null,
    lastActions,
    mustInclude: null,
    winnerId: state.winner === null ? null : id(state.winner),
    penalties,
    totals: { ...extras.totals },
    ...(extras.serverTime !== undefined ? { serverTime: extras.serverTime } : {}),
    ...(muushigView ? { muushig: muushigView } : {}),
  };
}
